import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Crown, TrendingUp, TrendingDown, 
  Search, RefreshCw, User, Calendar, CreditCard
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import AdminLayout from "@/components/AdminLayout";

const AdminTransactions = () => {
  const navigate = useNavigate();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    setLoading(true);
    try {
      const response = await api.getAdminSubscriptions();
      setSubscriptionData(response.data);
    } catch (error) {
      toast.error("Failed to load subscription data");
      // Set mock data for display
      setSubscriptionData({
        stats: {
          total_subscribers: 0,
          recovery_plus: 0,
          recovery_elite: 0,
          trials_active: 0,
          mrr: 0
        },
        recent_changes: []
      });
    } finally {
      setLoading(false);
    }
  };

  const filterOptions = [
    { label: "All", value: "all" },
    { label: "Upgrades", value: "upgrade" },
    { label: "Downgrades", value: "downgrade" },
    { label: "Cancellations", value: "cancel" },
    { label: "Trials", value: "trial" }
  ];

  const getFilteredChanges = () => {
    let changes = subscriptionData?.recent_changes || [];
    
    if (filter !== "all") {
      changes = changes.filter(c => c.type === filter);
    }
    
    if (search) {
      changes = changes.filter(c => 
        c.user_name?.toLowerCase().includes(search.toLowerCase()) ||
        c.user_email?.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    return changes;
  };

  const getChangeIcon = (change) => {
    if (change.type === "upgrade") return <TrendingUp className="w-4 h-4" />;
    if (change.type === "downgrade" || change.type === "cancel") return <TrendingDown className="w-4 h-4" />;
    return <Crown className="w-4 h-4" />;
  };

  const getChangeColor = (change) => {
    if (change.type === "upgrade") return "bg-green-100 text-green-600";
    if (change.type === "downgrade" || change.type === "cancel") return "bg-red-100 text-red-600";
    return "bg-blue-100 text-blue-600";
  };

  const tierLabels = {
    basic: "Free",
    gold: "Recovery+",
    recovery_elite: "Recovery Elite",
    recovery_plus: "Recovery+",
    recovery_elite: "Recovery Elite"
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM d, h:mm a");
    } catch {
      return "Unknown";
    }
  };

  return (
    <AdminLayout>
    <div className="bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm px-4 py-4 sticky top-[52px] z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-[#1C3A7A]">Subscriptions</h1>
              <p className="text-sm text-gray-500">Revenue & subscription changes</p>
            </div>
            <Button
              onClick={fetchSubscriptionData}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search by user name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10 bg-gray-100 border-none"
            />
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setFilter(option.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  filter === option.value
                    ? 'bg-[#1C3A7A] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FAB710] border-t-transparent"></div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto p-4 lg:p-6">
          {/* Stats Overview */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="w-10 h-10 bg-[#1C3A7A]/10 rounded-lg flex items-center justify-center mb-3">
                <User className="w-5 h-5 text-[#1C3A7A]" />
              </div>
              <p className="text-2xl font-bold text-[#1C3A7A]">
                {subscriptionData?.stats?.total_subscribers || 0}
              </p>
              <p className="text-sm text-gray-500">Total Subscribers</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="w-10 h-10 bg-[#FAB710]/10 rounded-lg flex items-center justify-center mb-3">
                <Crown className="w-5 h-5 text-[#FAB710]" />
              </div>
              <p className="text-2xl font-bold text-[#1C3A7A]">
                {subscriptionData?.stats?.recovery_plus || 0}
              </p>
              <p className="text-sm text-gray-500">Recovery+</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                <Crown className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-[#1C3A7A]">
                {subscriptionData?.stats?.recovery_elite || 0}
              </p>
              <p className="text-sm text-gray-500">Recovery Elite</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                <Calendar className="w-5 h-5 text-blue-500" />
              </div>
              <p className="text-2xl font-bold text-[#1C3A7A]">
                {subscriptionData?.stats?.trials_active || 0}
              </p>
              <p className="text-sm text-gray-500">Active Trials</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                <CreditCard className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-2xl font-bold text-[#1C3A7A]">
                ${(subscriptionData?.stats?.mrr || 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">Monthly Revenue</p>
            </div>
          </div>

          {/* Recent Changes List */}
          <div className="bg-white rounded-xl shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="font-semibold text-[#1C3A7A]">Recent Subscription Changes</h3>
            </div>
            
            <div className="divide-y divide-gray-100">
              {getFilteredChanges().length === 0 ? (
                <div className="text-center py-12">
                  <Crown className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No subscription changes found</p>
                  <p className="text-sm text-gray-400 mt-1">Changes will appear here as users upgrade or downgrade</p>
                </div>
              ) : (
                getFilteredChanges().map((change, idx) => (
                  <div key={idx} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getChangeColor(change)}`}>
                      {getChangeIcon(change)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 truncate">{change.user_name || 'Unknown User'}</p>
                      </div>
                      <p className="text-sm text-gray-500">
                        {change.type === 'upgrade' && `Upgraded to ${tierLabels[change.to_tier] || change.to_tier}`}
                        {change.type === 'downgrade' && `Downgraded to ${tierLabels[change.to_tier] || change.to_tier}`}
                        {change.type === 'cancel' && 'Cancelled subscription'}
                        {change.type === 'trial' && `Started ${tierLabels[change.to_tier] || change.to_tier} trial`}
                      </p>
                    </div>
                    
                    <div className="text-right">
                      <p className={`font-semibold ${
                        change.type === 'upgrade' ? 'text-green-600' : 
                        change.type === 'cancel' ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {change.type === 'upgrade' && '+'}
                        {change.type === 'cancel' && '-'}
                        ${change.amount || 0}/mo
                      </p>
                      <p className="text-xs text-gray-400">{formatDate(change.created_at)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminTransactions;
